import { MCP_SCOPE_DESCRIPTIONS } from "./scopes";
import { OAuthRequestError, type OAuthDiagnostic } from "./errors";

const KEY = "tripbears.oauthRequest";

// Retain the original bytes, including repeated resource parameters and provider
// signature fields. Parsing below is only for display/preflight; the API verifies
// the signature, PKCE, redirect registration and all authorization policy.
export function inspectOAuthQuery(query: string, now = Date.now()) {
  const params = new URLSearchParams(query);
  const exp = Number(params.get("exp"));
  if (
    params.getAll("sig").length !== 1 ||
    !params.get("sig") ||
    !params.get("client_id") ||
    !Number.isFinite(exp) ||
    exp * 1000 <= now
  ) {
    const reasons: string[] = [];
    if (params.getAll("sig").length !== 1 || !params.get("sig")) reasons.push("The request needs exactly one nonempty signature.");
    if (!params.get("client_id")) reasons.push("The client identity is missing.");
    if (!Number.isFinite(exp) || exp <= 0) reasons.push("The request expiry is missing or invalid.");
    else if (exp * 1000 <= now) reasons.push("The authorization request has expired. Start a new connection.");
    throw new OAuthRequestError(
      "This authorization request is invalid or has expired. Restart the connection from your MCP client.",
      "INVALID_OAUTH_REQUEST",
      [{ label: "Reason", value: reasons.join(" ") }],
    );
  }
  const scopes = (params.get("scope") ?? "").split(/\s+/).filter(Boolean);
  const unsupported = scopes.filter(scope => !Object.hasOwn(MCP_SCOPE_DESCRIPTIONS, scope));
  const missingRead = !scopes.includes("mcp:read");
  const missingWrite = scopes.includes("mcp:delete") && !scopes.includes("mcp:write");
  if (missingRead || unsupported.length || missingWrite) {
    const diagnostics: OAuthDiagnostic[] = [{ label: "Requested permissions (unverified)", value: scopes.join(", ") || "None" }];
    if (unsupported.length) diagnostics.push({ label: "Unsupported permissions", value: unsupported.join(", ") });
    if (unsupported.includes("mcp:trip-delete")) diagnostics.push({ label: "Reason", value: "mcp:trip-delete has been retired. Request mcp:delete instead." });
    if (missingRead) diagnostics.push({ label: "Missing required permission", value: "mcp:read" });
    if (missingWrite) diagnostics.push({ label: "Permission dependency", value: "mcp:delete requires mcp:write." });
    throw new OAuthRequestError("This client requested unsupported permissions. Restart from your MCP client.", "UNSUPPORTED_OAUTH_SCOPES", diagnostics);
  }
  return { clientId: params.get("client_id")!, scopes };
}
export function saveOAuthQuery(query: string) {
  inspectOAuthQuery(query);
  try {
    sessionStorage.setItem(KEY, query);
  } catch {
    throw new Error("Enable session storage in this browser to connect an MCP client.");
  }
  return query;
}
export function readOAuthQuery() {
  let query: string | null;
  try {
    query = sessionStorage.getItem(KEY);
  } catch {
    query = null;
  }
  if (!query)
    throw new Error(
      "No authorization request was found. Restart the connection from your MCP client.",
    );
  inspectOAuthQuery(query);
  return query;
}
export function captureOAuthQuery(search: string) {
  const query = search.startsWith("?") ? search.slice(1) : search;
  if (new URLSearchParams(query).has("sig")) return saveOAuthQuery(query);
  return readOAuthQuery();
}
export function clearOAuthQuery() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* Tab-local context only. */
  }
}
export function oauthRequestState(search: string) {
  try {
    return { query: captureOAuthQuery(search), error: null, failure: null };
  } catch (error) {
    return {
      query: null,
      error: error instanceof Error ? error.message : "Invalid authorization request.",
      failure: error,
    };
  }
}
